// Pfad: src/pages/SystemStatusAdmin.js
// Zweck/Funktion: Admin-Dashboard „Systemstatus“ mit GPT-Hinweis (oben) und Debug-Panel.
// - Auto-Refresh standardmäßig AUS; Intervalle: 30s, 1m, 2m, 5m, 30m, 60m
// - Initialer Abruf lädt Status; GPT-Hinweis wird NACH dem Rendern entkoppelt gestartet (kein await).
// - Robustes Token-Handling (LS/SS/Cookie) + Health-Fallback (public) bei fehlendem/ungültigem Token.
// - Kopierbares Debug-Panel (letzte 10 Ereignisse) – ohne Custom-Header (CORS-sicher).

import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config/config";
import AdminPageFrame from "../components/admin/AdminPageFrame";

const REQUEST_TIMEOUT_MS = 8000;

// ---------- UI-Helfer ----------
const Box = ({ children }) => (
  <div className="admin-system-box">{children}</div>
);
const Pill = ({ ok, labelTrue = "✓ OK", labelFalse = "❌ Fehler" }) => (
  <span
    className={`admin-system-pill ${
      ok ? "admin-system-pill-ok" : "admin-system-pill-error"
    }`}
  >
    {ok ? labelTrue : labelFalse}
  </span>
);
const Row = ({ label, value }) => (
  <div className="admin-system-row">
    <div className="admin-system-row__label">{label}</div>
    <div className="admin-system-row__value">{value}</div>
  </div>
);

// ---------- Token robust holen ----------
function getAuthToken() {
  const keys = ["token", "authToken", "jwt", "accessToken"];
  try {
    for (const store of [window.localStorage, window.sessionStorage]) {
      if (!store) continue;
      for (const k of keys) {
        const v = store.getItem(k);
        if (v) return v.replace(/^"|"$/g, "");
      }
    }
  } catch {}
  try {
    const m = document.cookie.match(
      /(?:^|;\s*)(token|authToken|jwt|accessToken)=([^;]+)/i
    );
    if (m && m[2]) return decodeURIComponent(m[2]);
  } catch {}
  return null;
}

// ---------- Axios-Instanz (ohne Custom-Header) ----------
const api = axios.create({ baseURL: API_BASE_URL, timeout: REQUEST_TIMEOUT_MS });
api.interceptors.request.use((config) => {
  (config.meta ||= {}).started = Date.now();
  const token = getAuthToken();
  if (token && !config.headers?.Authorization) {
    config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
  }
  console.groupCollapsed(
    `%c[SystemStatus][REQ] ${config.method?.toUpperCase()} ${config.baseURL || ""}${config.url}`,
    "color:#7bdff2;font-weight:bold;"
  );
  console.debug("headers:", config.headers);
  console.debug("timeout(ms):", config.timeout);
  console.debug("origin:", window.location.origin);
  console.groupEnd();
  return config;
});
api.interceptors.response.use(
  (res) => {
    const ms = (res.config.meta?.started && (Date.now() - res.config.meta.started)) || 0;
    console.groupCollapsed(
      `%c[SystemStatus][RES] ${res.status} ${res.config.method?.toUpperCase()} ${res.config.baseURL || ""}${res.config.url} (+${ms}ms)`,
      "color:#caffbf;font-weight:bold;"
    );
    console.debug("headers:", res.headers);
    console.debug("data:", res.data);
    console.groupEnd();
    return res;
  },
  (err) => {
    const cfg = err.config || {};
    const ms = (cfg.meta?.started && (Date.now() - cfg.meta.started)) || 0;
    console.groupCollapsed(
      `%c[SystemStatus][ERR] ${cfg.method?.toUpperCase?.() || "GET"} ${cfg.baseURL || ""}${cfg.url} (+${ms}ms)`,
      "color:#ffadad;font-weight:bold;"
    );
    console.warn("code:", err.code);
    console.warn("message:", err.message);
    if (err.response) {
      console.warn("status:", err.response.status);
      console.warn("res.headers:", err.response.headers);
      console.warn("res.data:", err.response.data);
    } else {
      console.warn("no HTTP response (Netzwerk/Timeout/Preflight?)");
    }
    console.groupEnd();
    return Promise.reject(err);
  }
);

// ---------- Debug-Snapshot-Builder ----------
function humanErr(err) {
  if (!err) return "Unbekannter Fehler.";
  if (err?.response?.data?.error) return err.response.data.error;
  if (err?.message) return err.message;
  return String(err);
}
function buildDebugSnapshot(tag, err, extra = {}) {
  const cfg = err?.response?.config || err?.config || {};
  return {
    tag,
    at: new Date().toISOString(),
    method: cfg.method?.toUpperCase?.() || "GET",
    url: `${cfg.baseURL || ""}${cfg.url || ""}`,
    timeoutMs: cfg.timeout,
    requestHeaders: cfg.headers || {},
    errorCode: err?.code || null,
    httpStatus: err?.response?.status || null,
    responseHeaders: err?.response?.headers || null,
    responseData: err?.response?.data || null,
    message: humanErr(err),
    apiBase: API_BASE_URL,
    ...extra
  };
}

// ---------- Hauptkomponente ----------
export default function SystemStatusAdmin() {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [ts, setTs] = useState(null);
  const [refreshMs, setRefreshMs] = useState(0); // Default: AUS
  const [loading, setLoading] = useState(false);

  const [gptAdvice, setGptAdvice] = useState("");
  const [adviceLoading, setAdviceLoading] = useState(false);
  const [adviceAutoTriggered, setAdviceAutoTriggered] = useState(false); // <-- NEU (Guard)

  const [debugOpen, setDebugOpen] = useState(false);
  const [debugList, setDebugList] = useState([]);

  const pushDebug = (snap) => setDebugList((prev) => [snap, ...prev].slice(0, 10));

  const fmtUptime = (s = 0) => {
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    return `${d}d ${h}h ${m}m`;
  };

  const safeVal = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));

  // ---- GPT-Hinweis separat laden (entkoppelt) ----
  const fetchAdvice = useCallback(async () => {
    setAdviceLoading(true);
    try {
      const res = await api.get(`/api/system-status?advice=1`);
      setGptAdvice(res?.data?.gptAdvice || "—");
    } catch (e) {
      pushDebug(buildDebugSnapshot("system-status-advice", e));
      setGptAdvice("⚠️ GPT-Hinweis konnte nicht geladen werden.");
    } finally {
      setAdviceLoading(false);
    }
  }, []);

  // ---- Systemstatus laden (kein await auf Advice!) ----
  const fetchStatus = useCallback(async (initial = false) => {
    setError(""); setLoading(true);
    const token = getAuthToken();

    // Ohne Token → öffentliche Health-Infos (kein Advice)
    if (!token) {
      try {
        const [z, d] = await Promise.all([
          api.get(`/api/healthz`),
          api.get(`/api/health/db`)
        ]);
        setStatus({
          ok: z?.data?.ok === true,
          port3001: true,
          dbConnected: !!d?.data?.dbConnected,
          gptConfigured: false,
          env: { nodeEnv: "n/a (public)", corsViaNginx: true, port: 3001, apiBaseUrl: API_BASE_URL },
          memory: {}, proxy: {}, db: { connected: !!d?.data?.dbConnected },
          _publicView: true
        });
        setTs(new Date());
        setError("Kein Token – öffentliche Health-Infos (eingeschränkt).");
      } catch (e) {
        pushDebug(buildDebugSnapshot("public-health", e));
        setError(humanErr(e));
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const res = await api.get(`/api/system-status`);
      setStatus(res.data);
      setTs(new Date());
      setLoading(false);           // <-- WICHTIG: sofort rendern
      if (initial && !adviceAutoTriggered) {
        // Advice entkoppelt (ohne await) anstoßen:
        setAdviceAutoTriggered(true);
        fetchAdvice().catch(() => {}); // Fehler schon in fetchAdvice behandelt
      }
    } catch (e) {
      pushDebug(buildDebugSnapshot("system-status", e));
      const is401 = e?.response?.status === 401;
      if (is401 || /token/i.test(humanErr(e))) {
        try {
          const [z, d] = await Promise.all([
            api.get(`/api/healthz`),
            api.get(`/api/health/db`)
          ]);
          setStatus({
            ok: z?.data?.ok === true,
            port3001: true,
            dbConnected: !!d?.data?.dbConnected,
            gptConfigured: false,
            env: { nodeEnv: "n/a (public)", corsViaNginx: true, port: 3001, apiBaseUrl: API_BASE_URL },
            memory: {}, proxy: {}, db: { connected: !!d?.data?.dbConnected },
            _publicView: true
          });
          setTs(new Date());
          setError("Token ungültig/abgelaufen – öffentliche Health-Infos (eingeschränkt).");
        } catch (e2) {
          pushDebug(buildDebugSnapshot("public-fallback", e2));
          setError(humanErr(e2));
        } finally {
          setLoading(false);
        }
        return;
      }
      setError(humanErr(e));
      setLoading(false);
    }
  }, [fetchAdvice, adviceAutoTriggered]);

  // Initial: Status laden; Advice wird durch fetchStatus(true) entkoppelt gestartet
  useEffect(() => { fetchStatus(true); }, [fetchStatus]);

  // Auto-Refresh: nur Status (ohne Advice)
  useEffect(() => {
    if (!refreshMs) return;
    const id = setInterval(() => fetchStatus(false), refreshMs);
    return () => clearInterval(id);
  }, [fetchStatus, refreshMs]);

  const publicView = !!status?._publicView;
  const tsText = ts ? ts.toLocaleTimeString() : "—";

  return (
    <AdminPageFrame
      title="Systemstatus"
      subtitle="Health, Datenbank, Pool und Debug-Snapshots in einem operativen Systemarbeitsraum."
      badge="System Operations"
    >
      <style>{`
        .admin-system-box {
          background: var(--dashboard-surface-muted);
          border: 1px solid var(--dashboard-border);
          border-radius: 20px;
          padding: 1rem;
          box-shadow: var(--dashboard-shadow-soft);
        }
        .admin-system-pill {
          display: inline-flex;
          align-items: center;
          border-radius: 999px;
          padding: 0.18rem 0.58rem;
          font-size: 0.82rem;
          font-weight: 600;
        }
        .admin-system-pill-ok {
          background: var(--dashboard-success-soft);
          color: var(--dashboard-success);
        }
        .admin-system-pill-error {
          background: var(--dashboard-danger-soft);
          color: var(--dashboard-danger);
        }
        .admin-system-row {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          padding: 0.42rem 0;
          border-bottom: 1px solid var(--dashboard-border);
        }
        .admin-system-row:last-child {
          border-bottom: 0;
        }
        .admin-system-row__label {
          color: var(--dashboard-text-secondary);
        }
        .admin-system-row__value {
          color: var(--dashboard-text-primary);
          font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
          word-break: break-word;
        }
      `}</style>
      <div className="max-w-6xl mx-auto p-2 text-white">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl md:text-3xl font-bold text-cyan-400">🩺 Systemstatus</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchStatus(false)}
            className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 rounded text-sm"
          >
            Aktualisieren
          </button>
          <select
            className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm"
            value={refreshMs}
            onChange={(e) => setRefreshMs(Number(e.target.value))}
            title="Auto-Refresh"
          >
            <option value={0}>Auto-Refresh: Aus</option>
            <option value={30000}>30s</option>
            <option value={60000}>1m</option>
            <option value={120000}>2m</option>
            <option value={300000}>5m</option>
            <option value={1800000}>30m</option>
            <option value={3600000}>60m</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-900/40 text-red-200 border border-red-700 px-4 py-3 rounded">
          <div className="font-semibold">Fehler</div>
          <div className="text-sm break-all">{error}</div>
        </div>
      )}

      {!status || loading ? (
        <Box><p className="text-gray-400">Lade Systemstatus…</p></Box>
      ) : (
        <>
          {/* GPT-Hinweis (oben) */}
          <Box>
            <div className="flex items-center justify-between mb-2">
              <div className="text-lg font-semibold">🤖 GPT-Hinweis</div>
              <button
                onClick={fetchAdvice}
                disabled={adviceLoading || publicView}
                className="px-3 py-1 bg-indigo-600 hover:bg-indigo-500 rounded text-sm disabled:opacity-50"
                title={publicView ? "In öffentlicher Ansicht nicht verfügbar" : "Hinweis aktualisieren"}
              >
                {adviceLoading ? "Lädt…" : "Refresh"}
              </button>
            </div>
            <div className="text-gray-200 text-sm whitespace-pre-line min-h-[7rem]">
              {publicView
                ? "— (in öffentlicher Ansicht nicht verfügbar)"
                : (gptAdvice || "Noch kein Hinweis.")}
            </div>
          </Box>

          {/* Ampeln & Node */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <Box>
              <div className="text-lg font-semibold mb-2">
                Ampeln {publicView && <span className="text-xs text-amber-300">(eingeschränkt)</span>}
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">🧭 Port {status?.env?.port || 3001} erreichbar</span>
                  <Pill ok={!!status.port3001} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">🧠 Datenbankverbindung</span>
                  <Pill ok={!!(status?.db?.connected ?? status?.dbConnected)} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">🤖 GPT‑API konfiguriert</span>
                  <Pill ok={!!status.gptConfigured} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">🛡️ CORS via NGINX</span>
                  <Pill ok={!!status?.env?.corsViaNginx} labelTrue="✓ NGINX" labelFalse="Express" />
                </div>
              </div>
            </Box>

            <Box>
              <div className="text-lg font-semibold mb-2">Node & Prozess</div>
              <Row label="Node.js Version" value={safeVal(status.nodeVersion)} />
              <Row label="Uptime" value={status.uptimeSeconds ? `${Math.floor(status.uptimeSeconds/86400)}d ${Math.floor((status.uptimeSeconds%86400)/3600)}h ${Math.floor((status.uptimeSeconds%3600)/60)}m` : "—"} />
              <Row label="Heap genutzt" value={status?.memory?.heapUsedMB != null ? `${status.memory.heapUsedMB} MB` : "—"} />
              <Row label="Heap reserviert" value={status?.memory?.heapTotalMB != null ? `${status.memory.heapTotalMB} MB` : "—"} />
              {status?.memory?.heapMaxMB != null && <Row label="Heap Limit (V8)" value={`${status.memory.heapMaxMB} MB`} />}
              <Row
                label="Node Heap Auslastung (genutzt/reserviert)"
                value={status?.memory?.heapUsedVsReservedPercent != null ? `${status.memory.heapUsedVsReservedPercent}%` : (status?.memory?.usedPercent != null ? `${status.memory.usedPercent}%` : "—")}
              />
              <Row
                label="Node Heap Auslastung (vs V8 Limit)"
                value={status?.memory?.heapUsedVsLimitPercent != null ? `${status.memory.heapUsedVsLimitPercent}%` : "—"}
              />
              <Row label="Node RSS" value={status?.memory?.rssMB != null ? `${status.memory.rssMB} MB` : "—"} />
              <Row
                label="System RAM Auslastung"
                value={status?.memory?.systemRamUsedPercent != null ? `${status.memory.systemRamUsedPercent}% (${status.memory.usedSystemRamMB} / ${status.memory.totalSystemRamMB} MB)` : "—"}
              />
              {status?.node?.eventLoopLag && (
                <>
                  <Row label="EventLoop Lag (aktuell)" value={`${safeVal(status.node.eventLoopLag.currentMs)} ms`} />
                  <Row label="EventLoop Lag (Ø 5min)" value={`${safeVal(status.node.eventLoopLag.avgMs5min)} ms`} />
                </>
              )}
            </Box>
          </div>

          {/* Datenbank */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <Box>
              <div className="text-lg font-semibold mb-2">Datenbank</div>
              <Row label="Verbunden" value={(status?.db?.connected ?? status?.dbConnected) ? "Ja" : "Nein"} />
              <Row label="Fehler" value={safeVal(status?.db?.error)} />
              <Row label="Version" value={safeVal(status?.db?.version)} />
              <Row label="DB-Zeit" value={safeVal(status?.db?.now)} />
              <Row label="wait_timeout" value={safeVal(status?.db?.wait_timeout)} />
              <Row label="interactive_timeout" value={safeVal(status?.db?.interactive_timeout)} />
            </Box>

            <Box>
              <div className="text-lg font-semibold mb-2">DB Latenzen (letzte 5 min)</div>
              <Row label="Letzte" value={`${safeVal(status?.db?.latencyMs?.last)} ms`} />
              <Row label="p50" value={`${safeVal(status?.db?.latencyMs?.p50)} ms`} />
              <Row label="p95" value={`${safeVal(status?.db?.latencyMs?.p95)} ms`} />
              <Row label="p99" value={`${safeVal(status?.db?.latencyMs?.p99)} ms`} />
            </Box>
          </div>

          {/* Pool- und Fehler-Statistik */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <Box>
              <div className="text-lg font-semibold mb-2">DB Pool</div>
              <Row label="Limit" value={safeVal(status?.db?.pool?.limit ?? status?.db?.pool?.connectionLimit)} />
              <Row label="Aktiv" value={safeVal(status?.db?.pool?.active)} />
              <Row label="Idle" value={safeVal(status?.db?.pool?.idle ?? status?.db?.pool?.free)} />
              <Row label="Queued" value={safeVal(status?.db?.pool?.queued)} />
              <Row label="Gesamt" value={safeVal(status?.db?.pool?.all)} />
            </Box>

            <Box>
              <div className="text-lg font-semibold mb-2">Fehler (5 min)</div>
              <Row label="Anzahl" value={safeVal(status?.errors?.last5min)} />
              {(status?.errors?.top3 || []).map((e, idx) => (
                <Row key={idx} label={`Top ${idx+1}`} value={`HTTP ${e.code} — ${e.count}×`} />
              ))}
            </Box>
          </div>

          {/* Debug-Panel */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <button
                onClick={() => setDebugOpen((v) => !v)}
                className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-sm"
              >
                {debugOpen ? "Debug-Details ausblenden" : "Debug-Details anzeigen"}
              </button>
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(JSON.stringify(debugList, null, 2));
                    alert("Debug-Daten in die Zwischenablage kopiert.");
                  } catch {
                    alert("Kopieren nicht möglich (Browser-Schutz). Bitte Konsole nutzen.");
                  }
                }}
                className="px-3 py-1 bg-gray-800 hover:bg-gray-700 rounded text-sm border border-gray-600"
              >
                Kopieren
              </button>
            </div>
            {debugOpen && (
              <pre className="bg-black/60 p-4 rounded text-xs text-gray-300 whitespace-pre-wrap break-all mt-2 max-h-80 overflow-y-auto">
{JSON.stringify(debugList, null, 2)}
              </pre>
            )}
          </div>

          <div className="text-right text-sm text-gray-400 mt-2">
            Letzte Aktualisierung: {tsText}
          </div>
        </>
      )}
      </div>
    </AdminPageFrame>
  );
}
